import { useInfiniteQuery } from '@tanstack/react-query';
import { Copy, Search as SearchIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { api } from '../../api/client';
import { useProject } from '../../api/hooks';
import type { TicketCard } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { StatusBadge } from '../../ui/Badge';
import { EmptyState, QueryView } from '../../ui/feedback';
import { Checkbox, Select } from '../../ui/forms';
import { LoadMore, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';
import { useToast } from '../../ui/toast';
import { PriorityBadge, TicketFlags } from './bits';

interface SearchResult {
  items: TicketCard[];
  total: number;
  projectCount: number;
  sort: string;
  nextCursor: string | null;
}

const FILTERS: { key: string; label: string; options: [string, string][] }[] = [
  {
    key: 'status',
    label: 'Status',
    options: [
      ['', 'Any status'],
      ['pending', 'Pending'],
      ['todo', 'Todo'],
      ['in_progress', 'In progress'],
      ['pending_release', 'Pending release'],
      ['closed', 'Closed'],
      ['pending,todo,in_progress,pending_release', 'Open'],
    ],
  },
  {
    key: 'resolution',
    label: 'Resolution',
    options: [
      ['', 'Any resolution'],
      ['done', 'Done'],
      ['rejected', 'Rejected'],
    ],
  },
  {
    key: 'type',
    label: 'Type',
    options: [
      ['', 'Any type'],
      ['issue', 'Issue'],
      ['feature_request', 'Feature request'],
    ],
  },
  {
    key: 'priority',
    label: 'Priority',
    options: [
      ['', 'Any priority'],
      ['urgent', 'Urgent'],
      ['high', 'High'],
      ['normal', 'Normal'],
      ['low', 'Low'],
    ],
  },
  {
    key: 'assignee',
    label: 'Assignee',
    options: [
      ['', 'Anyone'],
      ['me', 'Me'],
      ['unassigned', 'Unassigned'],
    ],
  },
  {
    key: 'reporter',
    label: 'Reporter',
    options: [
      ['', 'Anyone'],
      ['me', 'Me'],
    ],
  },
  {
    key: 'blocked',
    label: 'Blocked',
    options: [
      ['', 'Blocked or not'],
      ['true', 'Blocked'],
      ['false', 'Not blocked'],
    ],
  },
];

/** TKT-07: global and project search share filters, deterministic ordering, pagination and shareable URLs. */
export function SearchPage({ scoped }: { scoped?: boolean }) {
  const { key } = useParams();
  const project = useProject(scoped ? key : undefined);
  const [params, setParams] = useSearchParams();
  const toast = useToast();
  usePageTitle(scoped ? (project.data ? `${project.data.name} · Search` : null) : 'Search');
  useCrumbs(
    scoped
      ? [{ label: project.data?.name ?? key ?? '', to: `/projects/${key}` }, { label: 'Search' }]
      : [{ label: 'Workspace' }, { label: 'Search' }],
  );
  const [text, setText] = useState(params.get('q') ?? '');
  useEffect(() => setText(params.get('q') ?? ''), [params]);
  const query = new URLSearchParams(params);
  query.delete('cursor');
  const path = scoped ? `/projects/${key}/tickets` : '/search';
  const q = useInfiniteQuery({
    queryKey: ['search', path, query.toString()],
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams(query);
      if (pageParam) p.set('cursor', pageParam);
      return api.get<SearchResult>(`${path}?${p}`);
    },
    initialPageParam: '',
    getNextPageParam: (l) => l.nextCursor ?? undefined,
  });
  const set = (k: string, v: string) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v);
    else n.delete(k);
    setParams(n, { replace: true });
  };
  const shareUrl = `${location.origin}${location.pathname}?${query}`;
  return (
    <div className="page">
      <PageHeader
        title="Search"
        subtitle={
          scoped
            ? 'Search this project. Archived closed tickets are hidden unless included.'
            : 'Search titles, descriptions and comments across projects you can access.'
        }
      />
      <form
        className="row nowrap"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          set('q', text.trim());
        }}
      >
        <div
          className="row nowrap"
          style={{
            flex: 1,
            height: 44,
            padding: '0 14px',
            border: '1px solid var(--color-hairline)',
            borderRadius: 8,
            background: 'var(--color-canvas)',
          }}
        >
          <SearchIcon size={16} color="var(--color-muted)" aria-hidden />
          <input
            autoFocus
            aria-label="Search terms"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. offline queue, APP-42"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              font: 'inherit',
              fontSize: 15,
              color: 'var(--color-ink)',
            }}
          />
        </div>
        <Select
          aria-label="Sort"
          value={params.get('sort') ?? (params.get('q') ? 'relevance' : 'updated')}
          onChange={(e) => set('sort', e.target.value)}
          style={{ width: 180, height: 44 }}
        >
          {params.get('q') && <option value="relevance">Best match</option>}
          <option value="updated">Recently updated</option>
          <option value="created">Newest</option>
          <option value="priority">Priority</option>
          <option value="number">Ticket number</option>
        </Select>
        <button className="btn btn-primary" type="submit" style={{ height: 44 }}>
          Search
        </button>
      </form>
      <div className="filter-bar" role="group" aria-label="Filters">
        {FILTERS.map((f) => (
          <Select
            key={f.key}
            aria-label={f.label}
            value={params.get(f.key) ?? ''}
            onChange={(e) => set(f.key, e.target.value)}
          >
            {f.options.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </Select>
        ))}
        <Checkbox
          label="Include archived closed tickets"
          checked={params.get('archived') === 'true'}
          onChange={(on) => set('archived', on ? 'true' : '')}
        />
        {[...params.keys()].length > 0 && (
          <button className="btn btn-ghost sm" type="button" onClick={() => setParams({}, { replace: true })}>
            Clear filters
          </button>
        )}
      </div>
      <QueryView query={q}>
        {(data) => {
          const first = data.pages[0]!;
          const items = data.pages.flatMap((p) => p.items);
          return (
            <>
              <div className="row between small muted">
                <span>
                  {first.total} result{first.total === 1 ? '' : 's'}
                  {!scoped && ` across ${first.projectCount} project${first.projectCount === 1 ? '' : 's'}`} ·
                  ordered by {first.sort === 'relevance' ? 'best match' : first.sort}, then ticket ID
                </span>
                <button
                  className="btn btn-ghost sm"
                  type="button"
                  onClick={() => navigator.clipboard.writeText(shareUrl).then(() => toast('Link copied'))}
                >
                  <Copy size={13} /> Copy shareable link
                </button>
              </div>
              {items.length === 0 ? (
                <EmptyState icon={SearchIcon} title="No matching tickets">
                  Try fewer words or clear some filters.
                </EmptyState>
              ) : (
                <div className="table-wrap">
                  {items.map((r) => (
                    <Link
                      key={r.id}
                      to={`/projects/${r.projectKey}/tickets/${r.number}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '96px minmax(0,1fr) 150px 110px',
                        gap: 16,
                        padding: 16,
                        borderBottom: '1px solid var(--color-hairline-soft)',
                        color: 'var(--color-body)',
                        textDecoration: 'none',
                      }}
                    >
                      <span className="mono ink">{r.key}</span>
                      <span className="stack" style={{ gap: 4, minWidth: 0 }}>
                        <span className="strong">{r.title}</span>
                        {r.snippet && <span className="small muted">{highlight(r.snippet)}</span>}
                        <span className="row small" style={{ gap: 8 }}>
                          <PriorityBadge priority={r.priority} />
                          <TicketFlags t={r} />
                        </span>
                      </span>
                      <span>
                        <StatusBadge status={r.status} resolution={r.resolution} />
                      </span>
                      <span className="small muted" style={{ textAlign: 'right' }}>
                        <Timestamp value={r.updatedAt} dateOnly />
                      </span>
                    </Link>
                  ))}
                </div>
              )}
              <LoadMore
                hasMore={!!q.hasNextPage}
                onClick={() => q.fetchNextPage()}
                loading={q.isFetchingNextPage}
              />
            </>
          );
        }}
      </QueryView>
    </div>
  );
}

/** Renders ts_headline «markers» as <mark> without injecting HTML. */
function highlight(s: string) {
  return s.split(/(«[^»]*»)/).map((part, i) =>
    part.startsWith('«') ? (
      <mark
        key={i}
        style={{
          background: 'rgba(232,165,90,0.30)',
          color: 'var(--color-ink)',
          padding: '0 2px',
          borderRadius: 3,
        }}
      >
        {part.slice(1, -1)}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}
